import { HoneycombWebSDK, WebVitalsInstrumentation } from '@honeycombio/opentelemetry-web';
import { BACKEND_BASE_URL_OVERRIDE, DEPLOY_ENV } from '../common/js/site-config';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { TraceFlags } from '@opentelemetry/api';
import type { Span } from '@opentelemetry/api';

/**
 * Browser OpenTelemetry -> Honeycomb (service `bfx-web`). Loaded once per page
 * from baseof.html; patches fetch/XHR globally, so calls from every React bundle
 * are traced without touching their entry points. No-op unless
 * VITE_HONEYCOMB_API_KEY is set and the host is prod/preview (VITE_OTEL_LOCAL=true
 * also enables localhost). The key is public and ingest-only: a browser bundle
 * can't hold a secret.
 */

const PROD_HOSTS = ['borisfx.com', 'www.borisfx.com'];
// Origins that receive the W3C traceparent header. Anchored to avoid lookalike-host matches.
const BACKEND_URLS = [/^https:\/\/backend\.borisfx\.com(\/|$)/, /^http:\/\/localhost:8081(\/|$)/];
// The override is a plain origin, so escape every regex metacharacter in it
// (the character class lists them; '$&' re-inserts the matched character behind a
// backslash), then anchor it to the start and to a path boundary like the entries above.
if (BACKEND_BASE_URL_OVERRIDE) {
    BACKEND_URLS.push(new RegExp('^' + BACKEND_BASE_URL_OVERRIDE.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(/|$)'));
}
// Analytics beacons — excluded so each one doesn't become its own root trace.
const ANALYTICS_URLS = [
    /^https:\/\/www\.google\.com\//,
    /^https:\/\/[a-z0-9.-]*google-analytics\.com\//,
    /^https:\/\/[a-z0-9.-]*googletagmanager\.com\//,
    /^https:\/\/[a-z0-9.-]*doubleclick\.net\//,
];

function isLocalEnabled(): boolean {
    return ['1', 'true'].includes(import.meta.env.VITE_OTEL_LOCAL ?? '');
}

const RESOURCE_FETCH_MIN_DURATION_MS = 500;

// One resourceFetch span is emitted per page asset (~150 per page load), which would
// dominate event volume and cost; only slow asset loads are worth keeping. There is
// no supported way to drop a span, so clear its sampled flag before it ends — the
// batch processor then skips it at export. The callback runs before span.end().
function dropFastResourceFetch(span: Span, resource: PerformanceResourceTiming): void {
    if (resource.duration < RESOURCE_FETCH_MIN_DURATION_MS) {
        span.spanContext().traceFlags = TraceFlags.NONE;
    }
}

function initTelemetry(): void {
    const apiKey = import.meta.env.VITE_HONEYCOMB_API_KEY;
    if (!apiKey) return;

    const host = window.location.host;
    const isProd = PROD_HOSTS.includes(host);
    const isPreview = host.endsWith('.netlify.app');
    // Only honored off prod/preview, so a leaked flag can't alter a real deploy.
    const isLocal = !isProd && !isPreview && isLocalEnabled();
    // The QA build declares itself at build time; its hosts are neither prod nor preview.
    const isQa = !isProd && !isPreview && DEPLOY_ENV === 'qa';
    if (!isProd && !isPreview && !isLocal && !isQa) return;

    const deploymentEnvironment = isProd ? 'production' : isQa ? 'qa' : isLocal ? 'local' : 'preview';

    // Telemetry must never break the page.
    try {
        const sdk = new HoneycombWebSDK({
            apiKey,
            serviceName: 'bfx-web',
            serviceVersion: import.meta.env.VITE_COMMIT_REF || 'unknown',
            debug: isLocal,
            localVisualizations: isLocal,
            resourceAttributes: {
                'service.namespace': 'bfx',
                'deployment.environment': deploymentEnvironment,
            },
            // No sampleRate = send everything; revisit once volume is visible.
            // ignoreNetworkEvents drops the per-span resource-timing annotations,
            // each a separately billed Honeycomb event.
            instrumentations: [
                getWebAutoInstrumentations({
                    // axios uses XHR in the browser, so the XHR config covers axios-utility.
                    '@opentelemetry/instrumentation-fetch': {
                        propagateTraceHeaderCorsUrls: BACKEND_URLS,
                        ignoreUrls: ANALYTICS_URLS,
                        ignoreNetworkEvents: true,
                    },
                    '@opentelemetry/instrumentation-xml-http-request': {
                        propagateTraceHeaderCorsUrls: BACKEND_URLS,
                        ignoreUrls: ANALYTICS_URLS,
                        ignoreNetworkEvents: true,
                    },
                    '@opentelemetry/instrumentation-document-load': {
                        ignoreNetworkEvents: true,
                        applyCustomAttributesOnSpan: {
                            resourceFetch: dropFastResourceFetch,
                        },
                    },
                    // Click spans carry no app context; off until they attach meaningful attributes.
                    '@opentelemetry/instrumentation-user-interaction': {
                        enabled: false,
                    },
                }),
                new WebVitalsInstrumentation(),
            ],
        });

        sdk.start();
    } catch (e) {
        console.warn('[telemetry] failed to start, continuing without instrumentation', e);
    }
}

initTelemetry();
